import { useMemo, useState } from "react";
import { Alert, Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useHotelBookingContext } from "@/app/lib/context/HotelBookingContext";
import { useOptionalHotelDataContext } from "@/app/lib/context/HotelDataContext";
import { useHotelOffersContext } from "@/app/lib/context/HotelOffersContext";
import TravellersList from "@/app/ui/travellers/TravellersList";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import * as Sentry from "@sentry/react";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import { HotelTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import { useHotelSearch } from "@/app/lib/hooks/useHotelSearch";
import PageLoader from "@/app/ui/PageLoader";
import AnimatedLoader from "@/app/ui/AnimatedLoader";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { useCreatePendingInstallment } from "@/app/lib/hooks/useCreatePendingInstallment";
import { travelerTypeToTypeCode } from "@/app/lib/utils/transformTraveler";
import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import { SwipeableDrawer } from "@/app/ui/SwipeableDrawer";
import { DrawerTopBar } from "@/app/ui/Drawer";
import { TravellersSelection } from "@/app/ui/travellers/TravellersSelection";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/app/lib/utils/ageValidation";
import { TravellerSelectionStatusDrawer } from "@/app/ui/travellers/TravellerSelectionStatusDrawer";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import type { TravellerAgeGroup } from "@/app/lib/types/types";
import type { FindUserTravelersTravelers } from "@/app/lib/types/codegen";
import { difference } from "lodash-es";

export const Route = createFileRoute(
  "/_authenticated/_hotels/_hotel-offer/hotels/$id/travelers",
)({
  component: RouteComponent,
  staticData: {
    hotelStep: { stepNumber: 2 },
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  const { data: offers } = useHotelOffersContext();
  const { data: hotelData } = useOptionalHotelDataContext();
  const { searchResultKey, searchResultToken } = useHotelSearch();
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const { data: booking } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const { paymentType, selectedInstallments, refreshSession } =
    usePaymentContext();

  const {
    mutateAsync: createPendingInstallment,
    isPending,
    isError: isCreatePendingInstallmentError,
  } = useCreatePendingInstallment();

  const [isEligibilityLoading, setIsEligibilityLoading] = useState(false);

  const handleContinue = async () => {
    const currentOffer = offers?.find(
      (offer) =>
        offer.offerId === booking?.offerId &&
        offer.packageToken === booking?.packageToken,
    );

    if (!booking || !currentOffer || !searchResultKey || !searchResultToken) {
      return;
    }

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking?.offerId,
        price: booking?.price?.value,
      },
    );

    try {
      if (paymentType === "bnpl") {
        setIsEligibilityLoading(true);
        try {
          const { data: eligibilityData } = await refreshSession();
          if (eligibilityData?.isEligible && booking.price) {
            await createPendingInstallment({
              sessionId: eligibilityData.sessionId,
              amount: booking.price.value,
              countOfMonth: selectedInstallments,
            });
          }
        } finally {
          setIsEligibilityLoading(false);
        }
      }
      navigate({ to: "/hotels/$id/summary" });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travelDate = useMemo(
    () => (searchData ? new Date(searchData.dates.checkIn) : null),
    [searchData],
  );

  const selectedChildrenAges = useMemo(() => {
    const selectedChildrenIds = travellerSlots.CHD.filter(Boolean);
    return selectedChildrenIds.map((id) => {
      const foundTraveller = travellers?.find(
        (traveller) => traveller.id === id,
      );
      if (!foundTraveller || !travelDate) return null;
      return getTravellerAgeAtTravelDate(foundTraveller.birthDate, travelDate);
    });
  }, [travelDate, travellerSlots.CHD, travellers]);

  if (!searchData || !travelDate || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel travelers page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  if (isPending || isEligibilityLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
        <h3 className="mt-3 font-normal text-text-primary">
          {t("hotels.checkingRoomAvailability")}
        </h3>
      </PageLoader>
    );
  }

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  const childrenAges = searchData.rooms.flatMap((room) => room.childrenAges);

  const isSelectionDisabled = (traveller: FindUserTravelersTravelers) =>
    selectedTravellers.some((t) => t.id === traveller.id) ||
    (getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "child"
      ? !difference(childrenAges, selectedChildrenAges).includes(
          getTravellerAgeAtTravelDate(traveller.birthDate, travelDate),
        )
      : false);

  return (
    <div className="px-4">
      {isCreatePendingInstallmentError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("payment.pendingInstallmentError")}
        </Alert>
      )}

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        heightOffset={24}
        open={!!selectedSlot}
        onClose={() => setSelectedSlot(null)}
        backgroundColor="var(--color-surface-background)"
      >
        <DrawerTopBar
          onCloseClick={() => setSelectedSlot(null)}
          title={
            selectedSlot
              ? t("travellers.addFor", {
                  type: t("travellers.travellerTypeN", {
                    type: selectedSlot.ageGroup,
                    number: selectedSlot.index + 1,
                  }),
                })
              : null
          }
        />
        {selectedSlot ? (
          <TravellersList
            travellers={(travellers || []).filter(
              (traveller) =>
                travelerTypeToTypeCode(
                  getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
                ) === selectedSlot.ageGroup,
            )}
            travelDate={travelDate}
            selectedTravellerId={
              travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
            }
            isSelectionDisabled={isSelectionDisabled}
            onTravellerSelected={(travellerId) => {
              selectTraveller(selectedSlot.index, travellerId);
              setSelectedSlot(null);
            }}
          />
        ) : null}
      </SwipeableDrawer>

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          disabled={!allTravellersSelected}
          variant="contained"
          onClick={handleContinue}
        >
          {isEligibilityLoading ? t("common.loading") : t("hotels.continue")}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
